import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calendar, DollarSign, Star, MessageCircle, Bell, Repeat, X, AlertTriangle, CalendarClock, Heart, LayoutGrid, List, MapPin } from 'lucide-react';
import { ALL_TIME_SLOTS, endTimesFor, formatTime12 } from '../utils/timeSlots';
import Header from '../components/Header';
import BookingsCalendar from '../components/BookingsCalendar';
import CalendarSubscribe from '../components/CalendarSubscribe';
import AddToCalendarButton from '../components/AddToCalendarButton';
import PlacesManager from '../components/PlacesManager';
import CustomerBookingResponseBanner from '../components/CustomerBookingResponseBanner';
import DatePicker from '../components/DatePicker';
import { useLanguage } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookingsView, setBookingsView] = useState('list'); // 'list' | 'calendar'
  const [cancelModal, setCancelModal] = useState(null); // {booking, preview}
  const [rescheduleModal, setRescheduleModal] = useState(null); // {booking, newDate, newStart, newEnd}
  const [tipModal, setTipModal] = useState(null); // {booking, amount, customAmount}

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [bookingsRes, notificationsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/platform/settings`)
      ]);
      setBookings(bookingsRes.data);
      setNotifications(notificationsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCancelModal = (booking) => {
    // Compute preview client-side using settings
    const svcDt = new Date(`${booking.service_date}T${booking.start_time || booking.service_time || '00:00'}:00`);
    const hoursUntil = (svcDt.getTime() - Date.now()) / (1000 * 60 * 60);
    const isLate = hoursUntil < (settings?.cancellation_window_hours ?? 24);
    const total = booking.total_amount;
    const isPaid = ['confirmed', 'in_progress'].includes(booking.status);
    let preview;
    if (!isPaid || !isLate) {
      preview = { isLate: false, refundAmount: isPaid ? total : 0, fee: 0 };
    } else {
      const fee = Math.round(total * (settings?.cancellation_fee_percent ?? 50) / 100 * 100) / 100;
      preview = { isLate: true, refundAmount: Math.round((total - fee) * 100) / 100, fee };
    }
    setCancelModal({ booking, preview, hoursUntil: Math.max(0, hoursUntil) });
  };

  const confirmCancel = async () => {
    if (!cancelModal) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/${cancelModal.booking.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCancelModal(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Cancellation failed');
    }
  };

  const openTipModal = (booking) => {
    setTipModal({ booking, amount: 10, customAmount: '' });
  };

  const confirmTip = async () => {
    if (!tipModal) return;
    const finalAmount = tipModal.customAmount
      ? parseFloat(tipModal.customAmount)
      : tipModal.amount;
    if (!finalAmount || finalAmount < 1 || finalAmount > 500) {
      alert('Tip amount must be between $1 and $500');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/tips/checkout/session`,
        {
          booking_id: tipModal.booking.id,
          amount: Math.round(finalAmount * 100) / 100,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start tip checkout');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-success text-white';
      case 'completed': return 'bg-primary text-white';
      case 'pending_payment': return 'bg-accent text-white';
      case 'scheduled': return 'bg-secondary text-white';
      case 'awaiting_cleaner_response': return 'bg-blue-500 text-white';
      case 'change_proposed': return 'bg-amber-500 text-white';
      case 'declined_by_cleaner': return 'bg-red-500 text-white';
      case 'cancelled': return 'bg-error text-white';
      case 'cancelled_late': return 'bg-error text-white';
      default: return 'bg-gray-200 text-gray-700';
    }
  };

  const canCancel = (booking) => !['cancelled', 'cancelled_late', 'completed', 'declined_by_cleaner'].includes(booking.status);

  const openRescheduleModal = (booking) => {
    setRescheduleModal({
      booking,
      newDate: booking.service_date,
      newStart: booking.start_time || booking.service_time || '',
      newEnd: booking.end_time || ''
    });
  };

  const confirmReschedule = async () => {
    if (!rescheduleModal) return;
    if (!rescheduleModal.newDate || !rescheduleModal.newStart || !rescheduleModal.newEnd) {
      alert('Please select a date and time range');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/bookings/${rescheduleModal.booking.id}/reschedule`,
        {
          new_date: rescheduleModal.newDate,
          new_start_time: rescheduleModal.newStart,
          new_end_time: rescheduleModal.newEnd
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRescheduleModal(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Reschedule failed');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={setUser} />
      
      <div className="px-6 md:px-12 lg:px-16 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-text-primary mb-2" data-testid="customer-dashboard">
              Welcome back, {user.name}!
            </h1>
            <p className="text-text-secondary">Manage your bookings and view notifications</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b border-border">
            <button
              onClick={() => setActiveTab('bookings')}
              className={`pb-3 px-4 font-medium transition-all duration-300 ${
                activeTab === 'bookings'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-secondary hover:text-primary'
              }`}
              data-testid="bookings-tab"
            >
              My Bookings ({bookings.length})
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`pb-3 px-4 font-medium transition-all duration-300 ${
                activeTab === 'notifications'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-secondary hover:text-primary'
              }`}
              data-testid="notifications-tab"
            >
              {t('dashboard.notifications', 'Notifications')} ({notifications.filter(n => !n.read).length})
            </button>
            <button
              onClick={() => setActiveTab('places')}
              className={`pb-3 px-4 font-medium transition-all duration-300 inline-flex items-center gap-1.5 ${
                activeTab === 'places'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-secondary hover:text-primary'
              }`}
              data-testid="places-tab"
            >
              <MapPin className="w-4 h-4" />
              My Places
            </button>
          </div>

          {/* List / Calendar toggle (only on Bookings tab) */}
          {activeTab === 'bookings' && bookings.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <CalendarSubscribe />
              <div className="inline-flex bg-surface-muted rounded-full p-1" role="group" data-testid="bookings-view-toggle">
                <button
                  onClick={() => setBookingsView('list')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                    bookingsView === 'list' ? 'bg-text-primary text-white' : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid="view-list-button"
                  aria-pressed={bookingsView === 'list'}
                >
                  <List className="w-3.5 h-3.5" />
                  {t('common.viewList', 'List')}
                </button>
                <button
                  onClick={() => setBookingsView('calendar')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                    bookingsView === 'calendar' ? 'bg-text-primary text-white' : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid="view-calendar-button"
                  aria-pressed={bookingsView === 'calendar'}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  {t('common.viewCalendar', 'Calendar')}
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <p className="text-text-secondary">{t('common.loading', 'Loading...')}</p>
            </div>
          ) : activeTab === 'bookings' ? (
            <div>
              {bookings.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-text-secondary mb-6">{t('dashboard.noBookingsBody', "You don't have any bookings yet.")}</p>
                  <button onClick={() => navigate('/')} className="btn-primary" data-testid="find-cleaner-empty-cta">
                    {t('dashboard.findCleaner', 'Find a Cleaner')}
                  </button>
                </div>
              ) : bookingsView === 'calendar' ? (
                <BookingsCalendar
                  bookings={bookings}
                  perspective="customer"
                  onSelectBooking={(b) => {
                    // Scroll the matching card into view and flash it for context
                    setBookingsView('list');
                    setTimeout(() => {
                      const el = document.querySelector(`[data-booking-id="${b.id}"]`);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        el.classList.add('ring-2', 'ring-primary');
                        setTimeout(() => el.classList.remove('ring-2', 'ring-primary'), 1800);
                      }
                    }, 50);
                  }}
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {bookings.map((booking) => (
                    <div key={booking.id} data-booking-id={booking.id} className="bg-surface rounded-2xl p-6 border border-border transition-shadow" data-testid="booking-card">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-heading text-xl font-semibold text-text-primary mb-1">
                            {booking.cleaner_name}
                          </h3>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-text-secondary">{booking.service_type}</p>
                            {booking.is_recurring && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-light text-primary text-xs font-semibold rounded-full" data-testid="recurring-badge">
                                <Repeat className="w-3 h-3" />
                                {booking.recurrence_pattern}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(booking.status)}`}>
                          {booking.status.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                          <Calendar className="w-4 h-4" />
                          <span>{booking.service_date} at {booking.service_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                          <DollarSign className="w-4 h-4" />
                          <span className="font-semibold text-primary">${booking.total_amount.toFixed(2)}</span>
                        </div>
                      </div>

                      <CustomerBookingResponseBanner booking={booking} onResolved={fetchData} />

                      {booking.status === 'completed' && (
                        <div className="space-y-2" data-testid="completed-booking-actions">
                          {booking.tip_amount ? (
                            <div className="flex items-center justify-center gap-2 text-sm text-success bg-success/10 py-2.5 px-4 rounded-full" data-testid="tip-sent-badge">
                              <Heart className="w-4 h-4 fill-current" />
                              <span className="font-medium">{t('dashboard.youTipped', 'You tipped')} ${booking.tip_amount.toFixed(2)}</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => openTipModal(booking)}
                              className="w-full text-sm text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 font-medium py-2.5 px-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2"
                              data-testid="tip-cleaner-button"
                            >
                              <Heart className="w-4 h-4" />
                              {t('dashboard.tipCleaner', 'Tip Your Cleaner')}
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/cleaner/${booking.cleaner_id}`)}
                            className="btn-secondary w-full text-sm"
                            data-testid="leave-review-button"
                          >
                            {t('dashboard.leaveReview', 'Leave a Review')}
                          </button>
                        </div>
                      )}

                      {canCancel(booking) && (
                        <div className="space-y-2 mt-2" data-testid="booking-actions">
                          <AddToCalendarButton bookingId={booking.id} className="w-full" />
                          <button
                            onClick={() => openRescheduleModal(booking)}
                            className="w-full text-sm text-primary border border-primary/30 hover:bg-primary-light font-medium py-2.5 px-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2"
                            data-testid="reschedule-button"
                          >
                            <CalendarClock className="w-4 h-4" />
                            {t('dashboard.reschedule', 'Reschedule')}
                          </button>
                          <button
                            onClick={() => openCancelModal(booking)}
                            className="w-full text-sm text-error border border-error/30 hover:bg-error/5 font-medium py-2.5 px-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2"
                            data-testid="cancel-booking-button"
                          >
                            <X className="w-4 h-4" />
                            Cancel Booking
                          </button>
                        </div>
                      )}

                      {(booking.status === 'cancelled' || booking.status === 'cancelled_late') && booking.refund_amount > 0 && (
                        <div className="mt-2 text-xs text-text-secondary text-center" data-testid="cancel-refund-info">
                          Refunded ${booking.refund_amount.toFixed(2)}
                          {booking.cancellation_fee > 0 && ` • Fee: $${booking.cancellation_fee.toFixed(2)}`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeTab === 'notifications' ? (
            <div>
              {notifications.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-text-secondary">No notifications yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`bg-surface rounded-xl p-6 border ${
                        notification.read ? 'border-border' : 'border-primary bg-primary-light/20'
                      }`}
                      data-testid="notification-item"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full ${
                          notification.read ? 'bg-surface-muted' : 'bg-primary-light'
                        }`}>
                          <Bell className={`w-5 h-5 ${
                            notification.read ? 'text-text-secondary' : 'text-primary'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <p className="text-text-primary mb-1">{notification.message}</p>
                          <span className="text-xs text-text-secondary">
                            {new Date(notification.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div data-testid="places-tab-content">
              <PlacesManager />
            </div>
          )}
        </div>
      </div>

      {/* Cancellation Modal */}
      {cancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6" data-testid="cancel-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-start gap-4 mb-6">
              <div className={`p-3 rounded-full ${cancelModal.preview.isLate ? 'bg-red-50' : 'bg-primary-light'}`}>
                <AlertTriangle className={`w-6 h-6 ${cancelModal.preview.isLate ? 'text-error' : 'text-primary'}`} />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-text-primary mb-1">
                  {cancelModal.preview.isLate ? 'Late Cancellation' : 'Cancel Booking'}
                </h3>
                <p className="text-sm text-text-secondary">
                  Service in {cancelModal.hoursUntil.toFixed(1)} hours
                </p>
              </div>
            </div>

            <div className="bg-surface-muted rounded-xl p-4 mb-6 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-secondary">Booking total</span>
                <span className="font-semibold text-text-primary">${cancelModal.booking.total_amount.toFixed(2)}</span>
              </div>
              {cancelModal.preview.isLate && (
                <div className="flex justify-between text-error">
                  <span>Cancellation fee ({settings?.cancellation_fee_percent}%)</span>
                  <span className="font-semibold">- ${cancelModal.preview.fee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="font-semibold text-text-primary">Refund amount</span>
                <span className="font-heading text-lg font-bold text-primary" data-testid="cancel-refund-preview">
                  ${cancelModal.preview.refundAmount.toFixed(2)}
                </span>
              </div>
            </div>

            {cancelModal.preview.isLate && (
              <p className="text-xs text-text-secondary mb-6 leading-relaxed">
                Since you&apos;re cancelling within {settings?.cancellation_window_hours} hours of the service, a {settings?.cancellation_fee_percent}% cancellation fee applies. Of this fee, your cleaner receives {settings?.cancellation_cleaner_share_percent}% as compensation for the blocked time.
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setCancelModal(null)}
                className="flex-1 border border-border text-text-primary font-semibold py-3 rounded-full hover:bg-surface-muted"
                data-testid="cancel-modal-back"
              >
                Keep Booking
              </button>
              <button
                onClick={confirmCancel}
                className={`flex-1 font-semibold py-3 rounded-full text-white ${
                  cancelModal.preview.isLate ? 'bg-error hover:opacity-90' : 'bg-text-primary hover:bg-primary'
                }`}
                data-testid="confirm-cancel-button"
              >
                Cancel Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6" data-testid="reschedule-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-full bg-primary-light">
                <CalendarClock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-text-primary mb-1">Reschedule Booking</h3>
                <p className="text-sm text-text-secondary">
                  Pick a new date & time with {rescheduleModal.booking.cleaner_name}
                </p>
              </div>
            </div>

            <div className="bg-surface-muted rounded-xl p-4 mb-6 text-sm">
              <p className="text-text-secondary mb-1">Current slot</p>
              <p className="font-semibold text-text-primary">
                {rescheduleModal.booking.service_date} • {rescheduleModal.booking.start_time || rescheduleModal.booking.service_time} – {rescheduleModal.booking.end_time || '—'}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">New Date</label>
                <DatePicker
                  value={rescheduleModal.newDate}
                  onChange={(d) => setRescheduleModal({...rescheduleModal, newDate: d})}
                  placeholder="Pick a new date"
                  dataTestId="reschedule-date-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">From</label>
                  <select
                    value={rescheduleModal.newStart}
                    onChange={(e) => setRescheduleModal({...rescheduleModal, newStart: e.target.value, newEnd: rescheduleModal.newEnd <= e.target.value ? '' : rescheduleModal.newEnd})}
                    className="input-field w-full"
                    data-testid="reschedule-start-time"
                  >
                    <option value="">Start time</option>
                    {ALL_TIME_SLOTS.map(t => (
                      <option key={t} value={t}>{formatTime12(t)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">To</label>
                  <select
                    value={rescheduleModal.newEnd}
                    onChange={(e) => setRescheduleModal({...rescheduleModal, newEnd: e.target.value})}
                    className="input-field w-full"
                    disabled={!rescheduleModal.newStart}
                    data-testid="reschedule-end-time"
                  >
                    <option value="">End time</option>
                    {endTimesFor(rescheduleModal.newStart).map(t => (
                      <option key={t} value={t}>{formatTime12(t)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setRescheduleModal(null)}
                className="flex-1 border border-border text-text-primary font-semibold py-3 rounded-full hover:bg-surface-muted"
                data-testid="reschedule-cancel-button"
              >
                Keep Original
              </button>
              <button
                onClick={confirmReschedule}
                className="flex-1 font-semibold py-3 rounded-full text-white bg-text-primary hover:bg-primary"
                data-testid="confirm-reschedule-button"
              >
                Reschedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tip Modal */}
      {tipModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6" data-testid="tip-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-full bg-pink-50">
                <Heart className="w-6 h-6 text-pink-500 fill-current" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-text-primary mb-1">Tip {tipModal.booking.cleaner_name}</h3>
                <p className="text-sm text-text-secondary">
                  100% of your tip goes directly to your cleaner.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              {[5, 10, 20].map(preset => (
                <button
                  key={preset}
                  onClick={() => setTipModal({ ...tipModal, amount: preset, customAmount: '' })}
                  className={`py-3 rounded-xl font-semibold transition-all ${
                    !tipModal.customAmount && tipModal.amount === preset
                      ? 'bg-text-primary text-white'
                      : 'bg-surface-muted text-text-primary hover:bg-border'
                  }`}
                  data-testid={`tip-preset-${preset}`}
                >
                  ${preset}
                </button>
              ))}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-text-secondary mb-2">Or enter a custom amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary font-semibold">$</span>
                <input
                  type="number"
                  min="1"
                  max="500"
                  step="0.01"
                  value={tipModal.customAmount}
                  onChange={(e) => setTipModal({ ...tipModal, customAmount: e.target.value })}
                  placeholder="Custom amount"
                  className="input-field w-full pl-8"
                  data-testid="tip-custom-input"
                />
              </div>
              <p className="text-xs text-text-secondary mt-2">Min $1 • Max $500</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setTipModal(null)}
                className="flex-1 border border-border text-text-primary font-semibold py-3 rounded-full hover:bg-surface-muted"
                data-testid="tip-cancel-button"
              >
                Not Now
              </button>
              <button
                onClick={confirmTip}
                className="flex-1 font-semibold py-3 rounded-full text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90"
                data-testid="confirm-tip-button"
              >
                Send Tip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
